<script setup lang="ts">
import useMarkdown from './useMarkdown'
import { getFileType, openWindowWithWorkspace } from '@/utils/tool'
import emitter, { events } from '@/event-bus'
import { type IReferences, getReferences } from '@/components/common/intelligent-assistant-v2/src/api'
import { assistantListStore, historyListStore } from '@/components/common/intelligent-assistant-v2/src/store'

const asstListStore = assistantListStore()
const historyList = historyListStore()
const route = useRoute()

const renderMarkdown = useMarkdown()

const isFileDetailPage = computed(() => {
  return route.name === 'KnowledgeFileDetail'
})

const isAiAssistantPage = computed(() => {
  return route.path.includes('/ai-assistant')
})

const routeFileId = computed(() => {
  return Number(route.params.fileId)
})

const popoverX = ref(-1000)
const popoverY = ref(-1000)
const showPopover = ref(false)
const triggerElementRef = ref<HTMLElement>()

const isContentMouseEnter = ref(false)
const contentMouseEnterTimer = ref()
const triggerMouseLeaveTimer = ref()

// 引用内容
const eferenceInfo = ref({
  index: 0,
  fileId: 0,
  fileName: '',
  page: 0,
  uuid: '',
  info: '',
})
// 文件类型
const fileType = computed(() => {
  return `.${getFileType(eferenceInfo.value.fileName)}`
})

// 缓存引用内容
const cacheMap = new Map()
// 获取引用内容
async function getReferenceContent(params: IReferences) {
  const res = await getReferences(params)
  return res
}

function getMessageByUuid(uuid: string) {
  const messageList = asstListStore.asstMessageList.concat(historyList.historyMessageList)
  return messageList.find(message => message.uuid === uuid)
}

function handleTriggerMouseLeave() {
  triggerMouseLeaveTimer.value = setTimeout(() => {
    if (isContentMouseEnter.value) {
      return
    }

    showPopover.value = false

    if (triggerElementRef.value) {
      triggerElementRef.value.removeEventListener('mouseleave', handleTriggerMouseLeave)
    }

    cleanUp()
  }, 500)
}

async function handleTriggerMouseOver(e: Event) {
  const targetElement = e.target as HTMLElement

  if (targetElement && targetElement.classList && targetElement.classList.contains('quote-popover-trigger')) {
    // 初始化先隐藏和清除定时器
    showPopover.value = false
    isContentMouseEnter.value = false

    if (triggerMouseLeaveTimer.value) {
      clearTimeout(triggerMouseLeaveTimer.value)
      triggerMouseLeaveTimer.value = null
    }
    if (contentMouseEnterTimer.value) {
      clearTimeout(contentMouseEnterTimer.value)
      contentMouseEnterTimer.value = null
    }

    targetElement.classList.add('quote-popover-trigger-hover')

    const rect = targetElement.getBoundingClientRect()
    triggerElementRef.value = targetElement
    popoverX.value = rect.left + rect.width / 2
    popoverY.value = rect.top + 2

    const index = targetElement.dataset.idx || ''
    const id = targetElement.dataset.id || ''
    const role = targetElement.dataset.role || ''
    const uuid = targetElement.dataset.uuid || ''

    const cacheKey = `${id}-${index}-${role}-${uuid}`
    const cache = cacheMap.get(cacheKey)

    triggerElementRef.value.addEventListener('mouseleave', handleTriggerMouseLeave)

    if (cache) {
      // 如果是已有的缓存内容，直接使用
      eferenceInfo.value = cache
    } else {
      if (isFileDetailPage.value) {
        const res = await getReferenceContent({
          requestId: id,
          page: index,
          role,
        })

        if (res.content && res.content.referenceText) {
          eferenceInfo.value = {
            fileId: res.content.fileId,
            fileName: res.content.fileName,
            page: res.page,
            info: res.content.referenceText,
            uuid,
            index: Number(index),
          }
          cacheMap.set(cacheKey, eferenceInfo.value)
        } else {
          return
        }
      } else {
        const message = getMessageByUuid(uuid)
        const doc = isFileDetailPage.value ? message?.docList[0] : message?.docList[Number(index) - 1]

        // console.log('doc', doc, uuid, message?.docList)
        if (doc) {
          eferenceInfo.value = {
            fileId: doc.fileId || 0,
            fileName: doc.fileName || '',
            page: doc.page || 0,
            info: doc.info || '',
            uuid,
            index: Number(index),
          }
          cacheMap.set(cacheKey, eferenceInfo.value)
        } else {
          return
        }
      }

      // 获取内容
      // const res = await getReferenceContent({
      //   requestId: id,
      //   page: index,
      //   role,
      // })

      // if (!res.content.referenceText) {
      //   return
      // }

      // eferenceInfo.value.fileId = res.content.fileId
      // eferenceInfo.value.fileName = res.content.fileName
      // eferenceInfo.value.referenceText = res.content.referenceText
      // eferenceInfo.value.page = res.page
      // eferenceInfo.value.uuid = uuid
      // cacheMap.set(cacheKey, eferenceInfo.value)
    }

    showPopover.value = true
  }
}

function handleContentMouseEnter() {
  isContentMouseEnter.value = true

  if (contentMouseEnterTimer.value) {
    clearTimeout(contentMouseEnterTimer.value)
  }
}

function handleContentMouseLeave() {
  contentMouseEnterTimer.value = setTimeout(() => {
    isContentMouseEnter.value = false
    showPopover.value = false
    cleanUp()
  }, 300)
}

function cleanUp() {
  setTimeout(() => {
    eferenceInfo.value = {
      fileId: 0,
      fileName: '',
      page: 0,
      uuid: '',
      info: '',
      index: 0,
    }
    if (triggerElementRef.value) {
      triggerElementRef.value.classList.remove('quote-popover-trigger-hover')
    }
  }, 200)
}

function handleFooterClick() {
  // 如果当前页面是文件详情页
  // id和当前引用内容文件 id一致，切换到源文件 tab，定位到当前引用内容页码
  // id和当前引用内容文件 id不一致，则打开新窗口，跳转至文件详情页
  const fileId = eferenceInfo.value.fileId
  if (isFileDetailPage.value) {
    if (routeFileId.value === fileId) {
      // 切换到源文件 tab，定位到当前引用内容页码
      // 全局事件广播，交给外部处理
      console.log('scrollToPosition', eferenceInfo.value.page)
      emitter.emit(events.appAiQuoteScrollToPosition, eferenceInfo.value.page)
      return
    } else {
      // 打开新窗口，跳转至文件详情页
      openWindowWithWorkspace(`/knowledge-file-detail/${fileId}?page=${eferenceInfo.value.page}`)
    }
  } else {
    // 如果不是 ai 助手页面 打开新窗口，跳转至文件详情页
    openWindowWithWorkspace(`/knowledge-file-detail/${fileId}?page=${eferenceInfo.value.page}`)
    // if (!isAiAssistantPage.value) {
    //   openWindowWithWorkspace(`/knowledge-file-detail/${fileId}?page=${eferenceInfo.value.page}`)
    // } else {
    //   console.log('ai 助手页面')
    //   openQuoteDialog()
    // }
  }

  // 全局事件广播，交给外部处理，目前主要知识助手页面增加了引用弹窗，需要定位对应的引用
  emitter.emit(events.appAiQuotePopoverOutLinkClick, eferenceInfo.value)
}

function openQuoteDialog() {
  const message = getMessageByUuid(eferenceInfo.value.uuid)

  console.log('message', message)
  console.log('eferenceInfo.value', eferenceInfo.value)

  if (message) {
    emitter.emit(events.showAppAiQuoteDialog, {
      index: eferenceInfo.value.index,
      quoteList: message.docList || [],
    })
  }
}
function handleDocumentScroll() {
  showPopover.value = false
  cleanUp()
}

onMounted(() => {
  document.addEventListener('mouseover', handleTriggerMouseOver)
  document.addEventListener('scroll', handleDocumentScroll)
})

onUnmounted(() => {
  document.removeEventListener('mouseover', handleTriggerMouseOver)
  document.removeEventListener('scroll', handleDocumentScroll)
})
</script>

<template>
  <NPopover :show="showPopover && !!eferenceInfo.info" :x="popoverX" :y="popoverY" trigger="manual" :animated="false"
    class="app-ai-quote-popover">
    <div class="quote-popover-content" @mouseenter="handleContentMouseEnter" @mouseleave="handleContentMouseLeave">
      <n-scrollbar style="max-height: 180px">
        <div class="body" @click.stop="openQuoteDialog" v-html="renderMarkdown(eferenceInfo.info)" />
        <!-- <div class="body" @click="openQuoteDialog" v-html="eferenceInfo.info" /> -->
      </n-scrollbar>

      <div class="footer" @click="handleFooterClick">
        <FileThumbnail :url="eferenceInfo.fileName" :size="16" />
        <div class="file-name ellipsis">
          {{ eferenceInfo.fileName }}
        </div>
        <!-- <div class="file-type">
          {{ fileType }}
        </div> -->
        <div class="out-link">
          <i class="icon" />
        </div>
      </div>
    </div>
  </NPopover>
</template>

<style lang="scss">
@import 'katex/dist/katex.min.css';
/* 在这里添加KaTeX的CSS样式 */
@import '@/styles/markdown.scss';

.app-ai-quote-popover {
  padding: 0 !important;
  // width: 358px;
  background: #FFFFFF;
  border-radius: 8px !important;
  border: 1px solid #ECECEC;
  box-shadow: 0px 4px 20px 1px rgba(0, 0, 0, 0.16);
}

.quote-popover-content {
  padding: 16px 16px 12px !important;
  width: 326px;

  .body {
    color: #242F57;
    font-size: 12px;
    line-height: 18px;
    white-space: pre-wrap;
  }

  .footer {
    margin-top: 8px;
    height: 22px;
    display: flex;
    align-items: center;
    color: #8FA1B2;
    font-size: 12px;

    &:hover {
      color: #242F57;
      cursor: pointer;

      .out-link {
        .icon {
          border-color: #4050ff;

          &::before {
            background: #4050ff;
          }
        }
      }
    }

    .file-name {
      margin-left: 3px;
      flex: 1;
      min-width: 0;
    }

    .file-type {
      margin-left: 3px;
    }

    .out-link {
      width: 40px;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      .icon {
        width: 7px;
        height: 7px;
        border-top: 1px solid #425466;
        border-right: 1px solid #425466;
        position: relative;

        &::before {
          content: '';
          position: absolute;
          width: 8px;
          height: 1px;
          right: -1px;
          top: 2px;
          background: #425466;
          transform: rotate(145deg);
        }
      }
    }

  }
}
</style>
